import { APP_BASE_HREF } from '@angular/common'
import { Injectable, inject, signal } from '@angular/core'

import { OAuthService } from 'angular-oauth2-oidc'

import { Observable, firstValueFrom, of, switchMap } from 'rxjs'

import { FlxModalService } from '@flowx/angular-ui-toolkit'
import { config, getApplicationSettings, initAxios, setConfig, updateConfig } from '@flowx/core-sdk'
import { environment } from '@flowx/designer/environment'
import { FX_ORGANIZATION_CODE_TOKEN } from '@flowx/designer/modules/auth/auth.constants'
import { InstallOptions, OrganizationPublicInfo } from '@flowx/designer/modules/auth/auth.types'
import { FX_ENVIRONMENT_DEFINITION } from '@flowx/designer/modules/auth/environment-definition.constants'
import { AuthApiService } from '@flowx/designer/shared/api/auth.api'
import { InformationModalComponent } from '@flowx/designer/shared/components/information-modal-component/information-modal-component'

export interface StartProcessParams {
  workspaceId: string
  applicationId: string
  applicationName: string
}

/** What launching a project did: opened its entry point, or found none set. */
export type EntryPointLaunchResult = 'launched' | 'entry_point_missing'

/** Where a launch reads the entry point: the version selected on Config, or the active policy's. */
export type EntryPointSource = 'selected_version' | 'active_policy'

const ENTRY_POINT_NOT_SET_MESSAGES: Record<EntryPointSource, string> = {
  selected_version:
    'This current version has no entry point set. Set a <strong>Process</strong> or <strong>UI Flow</strong> as the entry point.',
  active_policy: `The version served by the active policy has no entry point set, so the project can't be launched. Set a <strong>Process</strong> or <strong>UI Flow</strong> as the entry point, then make sure the active policy serves a version that includes it.`,
}

/** Tells the maker that the project can't be launched because its version has no entry point. */
export const showEntryPointNotSet = (
  modalService: Pick<FlxModalService, 'show'>,
  projectName: string,
  source: EntryPointSource
): void => {
  modalService.show(
    InformationModalComponent,
    { backdropClass: 'overlay-backdrop' },
    {
      title: 'Entry Point not set',
      subtitle: `<i class="ph ph-folder"></i> ${projectName}`,
      message: ENTRY_POINT_NOT_SET_MESSAGES[source],
    }
  )
}

@Injectable({
  providedIn: 'root',
})
export class StartProcessService {
  #oauthService = inject(OAuthService)
  #authApiService = inject(AuthApiService)
  #flxModalService = inject(FlxModalService)
  #organizationCode = inject(FX_ORGANIZATION_CODE_TOKEN)
  #baseHref = inject(APP_BASE_HREF)
  #accessToken = signal(this.#oauthService.getAccessToken())
  #environmentDefinition = inject(FX_ENVIRONMENT_DEFINITION)

  async startProcess(params: StartProcessParams): Promise<EntryPointLaunchResult> {
    const { workspaceId, applicationId, applicationName } = params
    const appInfo = { appId: applicationId }
    const apiUrl = environment.baseUrl
    const options = { authToken: this.#accessToken(), apiUrl, appInfo, workspaceId }

    initAxios()

    if (!config()) {
      setConfig({ ...options })
    } else {
      updateConfig({ ...options })
    }

    const applicationSettings = await getApplicationSettings(apiUrl, {
      appInfo,
      processInstanceUuid: null,
      workspaceId,
    })

    if (applicationSettings?.buildSettings?.entrypoints) {
      // Awaited, so `launched` is only returned once the app has opened; a failed lookup rejects.
      const { genericRuntimeUri } = await firstValueFrom(
        this.#environmentDefinition.pipe(
          switchMap((environmentDefinition) => {
            const endpoint: Record<InstallOptions, Observable<OrganizationPublicInfo>> = {
              [InstallOptions.ON_PREM]: of(environmentDefinition.organizationInfo),
              [InstallOptions.SAAS]: this.#authApiService.getOrganizationPublicInfo(
                this.#organizationCode
              ),
            }

            return endpoint[environmentDefinition.installation]
          })
        )
      )

      const containerAppUrl = `${genericRuntimeUri}${this.#baseHref}${this.#organizationCode}/project?&workspaceId=${workspaceId}&projectId=${applicationId}`
      console.log('open container app at: ' + containerAppUrl)
      window.open(containerAppUrl, '_blank')

      return 'launched'
    }

    showEntryPointNotSet(this.#flxModalService, applicationName, 'active_policy')

    return 'entry_point_missing'
  }
}
